import { Icon } from '../components/Icon'
import { formatDuration, formatLength } from '../lib/format'
import type { Theory, TheorySummary } from '../lib/types'
import { useShare } from '../lib/useShare'
import { RATES, useAudio } from './AudioProvider'
import { Scrubber } from './Scrubber'

/**
 * The big player at the top of a theory. It drives the shared audio element
 * rather than owning one of its own, so hitting play here and then wandering
 * off to the archive hands playback to the docked mini player mid-sentence.
 */
export function TheoryPlayer({ theory }: { theory: Theory }) {
  const audio = useAudio()
  const { share, copied } = useShare()
  if (!theory.audio) return null

  const current = audio.isCurrent(theory.slug)

  /** Copies a link that opens the page and jumps straight to this moment. */
  const shareMoment = () => {
    const at = Math.floor(current ? audio.currentTime : 0)
    const url = new URL(window.location.href)
    url.hash = ''
    // 0 is the start of the recording, which is just the page — no point
    // handing someone a link with a redundant ?t=0 on the end of it.
    if (at > 0) url.searchParams.set('t', String(at))
    else url.searchParams.delete('t')
    void share(url.toString(), `${theory.title} at ${formatDuration(at)}`)
  }
  const duration = current && audio.duration ? audio.duration : (theory.audio.seconds ?? 0)
  const cycleRate = () => {
    const index = RATES.indexOf(audio.rate as (typeof RATES)[number])
    audio.setRate(RATES[(index + 1) % RATES.length] ?? 1)
  }

  return (
    <section className="theory-player" aria-label="The recording">
      <div className="theory-player-row">
        <button
          type="button"
          className="theory-player-play"
          onClick={() => audio.play(theory as TheorySummary)}
          aria-label={current && audio.playing ? 'Pause' : 'Play the recording'}
        >
          {current && audio.loading ? (
            <span className="spinner" />
          ) : (
            <Icon name={current && audio.playing ? 'pause' : 'play'} size={26} />
          )}
        </button>

        <div className="theory-player-main">
          <Scrubber
            currentTime={current ? audio.currentTime : 0}
            duration={duration}
            buffered={current ? audio.buffered : 0}
            onSeek={audio.seek}
            label="Seek within the recording"
          />
          <p className="theory-player-times mono">
            <span>{formatDuration(current ? audio.currentTime : 0)}</span>
            <span className="muted">
              {current ? formatDuration(duration) : (formatLength(theory.audio.seconds) ?? '—')}
            </span>
          </p>
        </div>

        <div className="theory-player-aux">
          <button
            type="button"
            className="btn btn-subtle btn-icon theory-player-skip"
            onClick={() => audio.skip(-15)}
            disabled={!current}
            aria-label="Back 15 seconds"
            title="Back 15 seconds"
          >
            <Icon name="back15" size={18} />
          </button>
          <button
            type="button"
            className="btn btn-subtle btn-icon theory-player-skip"
            onClick={() => audio.skip(30)}
            disabled={!current}
            aria-label="Forward 30 seconds"
            title="Forward 30 seconds"
          >
            <Icon name="forward30" size={18} />
          </button>
          <button
            type="button"
            className="btn btn-subtle btn-sm rate-button"
            onClick={cycleRate}
            aria-label={`Playback speed ${audio.rate}×. Click to change.`}
            title="Playback speed"
          >
            {audio.rate}×
          </button>
          <button
            type="button"
            className="btn btn-subtle btn-icon"
            onClick={shareMoment}
            aria-label="Copy a link to this moment"
            title={current ? `Copy a link to ${formatDuration(audio.currentTime)}` : 'Copy a link to this moment'}
          >
            <Icon name={copied ? 'check' : 'link'} size={18} />
          </button>
          <a
            className="btn btn-subtle btn-icon"
            href={theory.audio.url}
            download
            aria-label="Download the recording"
            title="Download"
          >
            <Icon name="download" size={18} />
          </a>
        </div>
      </div>

      {current && audio.error && (
        <p className="error-text" role="alert">
          {audio.error}
        </p>
      )}
    </section>
  )
}
